import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Button, Checkbox, Stack, Tooltip } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { EmailTemplateDto } from '../../../services/api/sharedTypes/Email'
import CustomTextField from '../../../components/common/CustomTextField'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { getEmailExplanationFromEmailMessageType } from '../../../utils/email-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'subject',
        numeric: false,
        disablePadding: false,
        label: 'Předmět',
    },
    {
        id: 'bodyTemplate',
        numeric: false,
        disablePadding: false,
        label: 'Text zprávy',
    },
    {
        id: 'isEnabled',
        numeric: false,
        disablePadding: false,
        label: 'Povolit odesílání',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface EmailTemplateTableProps {
    emails: EmailTemplateDto[]
    onSave: (id: string, subject: string, emailBody: string, isEnabled: boolean) => void
}

const EmailTemplateTable: React.FC<EmailTemplateTableProps> = ({ emails, onSave }: EmailTemplateTableProps) => {
    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<string>('eventName')
    const [localEmails, setLocalEmails] = React.useState<EmailTemplateDto[]>(emails)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    return (
        <>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={localEmails.length}
                        />
                        <TableBody>
                            {stableSort(
                                localEmails.map((email) => {
                                    return {
                                        ...email,
                                        createdAt: new Date(email.createdAt).toLocaleDateString(),
                                        lastModifiedAt: new Date(email.lastModifiedAt).toLocaleDateString(),
                                        isEnabled: email.isEnabled ? 1 : 0,
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow tabIndex={-1} key={index}>
                                        <CustomTableCell multiline style={{ verticalAlign: 'top' }}>
                                            <Stack direction='row' alignItems='center' mt={3} spacing={2}>
                                                <Tooltip title={getEmailExplanationFromEmailMessageType(row.type)}>
                                                    <HelpOutlineIcon color='disabled' fontSize='small' />
                                                </Tooltip>
                                                <CustomTextField
                                                    size='small'
                                                    multiline
                                                    fullWidth
                                                    maxRows={8}
                                                    value={row.subject}
                                                    style={{
                                                        minWidth: '310px',
                                                    }}
                                                    onChange={(e) => {
                                                        const newEmails = localEmails.map((email) => {
                                                            if (email.id === row.id) {
                                                                return {
                                                                    ...email,
                                                                    subject: e.target.value,
                                                                }
                                                            }
                                                            return email
                                                        })
                                                        setLocalEmails(newEmails)
                                                    }}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                        <CustomTableCell multiline>
                                            <CustomTextField
                                                size='small'
                                                fullWidth
                                                multiline
                                                rows={8}
                                                value={row.bodyTemplate}
                                                style={{
                                                    marginTop: theme.spacing(3),
                                                    marginBottom: theme.spacing(3),
                                                    minWidth: '770px',
                                                }}
                                                onChange={(e) => {
                                                    const newEmails = localEmails.map((email) => {
                                                        if (email.id === row.id) {
                                                            return {
                                                                ...email,
                                                                bodyTemplate: e.target.value,
                                                            }
                                                        }
                                                        return email
                                                    })
                                                    setLocalEmails(newEmails)
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell multiline style={{ verticalAlign: 'top' }}>
                                            <Checkbox
                                                checked={row.isEnabled === 1 ? true : false}
                                                onChange={(e) => {
                                                    const newEmails = localEmails.map((email) => {
                                                        if (email.id === row.id) {
                                                            return {
                                                                ...email,
                                                                isEnabled: e.target.checked,
                                                            }
                                                        }
                                                        return email
                                                    })
                                                    setLocalEmails(newEmails)
                                                }}
                                                style={{ marginTop: theme.spacing(1), marginLeft: theme.spacing(2) }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell multiline style={{ verticalAlign: 'top' }}>
                                            <Button
                                                style={{ marginTop: theme.spacing(2) }}
                                                variant='contained'
                                                onClick={() =>
                                                    onSave(
                                                        row.id,
                                                        row.subject,
                                                        row.bodyTemplate,
                                                        row.isEnabled === 1 ? true : false
                                                    )
                                                }
                                            >
                                                Uložit
                                            </Button>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

export default EmailTemplateTable
